import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import CategoryTicketDisplay from '../components/CategoryTicketDisplay';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookingDetails();
  }, [searchParams]);

  // Auto-download PDF when booking details are loaded
  useEffect(() => {
    if (bookingDetails && !loading) {
      const timer = setTimeout(() => {
        generatePDF();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [bookingDetails, loading]);

  const generatePDF = async () => {
    if (!bookingDetails) return;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/pdf/generate-pdf`, {
        bookingId: bookingDetails.bookingId
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket-${bookingDetails.bookingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF generation error:', error);
    }
  };

  const fetchBookingDetails = async () => {
    try {
      const bookingId = searchParams.get('bookingId') || searchParams.get('txnid') || searchParams.get('orderId');
      const amount = searchParams.get('amount');
      
      console.log('URL params:', { bookingId, amount });
      console.log('All search params:', Object.fromEntries(searchParams));
      
      if (bookingId) {
        console.log('Fetching booking details for:', bookingId);
        try {
          const response = await axios.get(`${API_BASE_URL}/api/bookings/${bookingId}?populate=site`);
          console.log('API Response:', response.data);
          
          if (response.data.success) {
            const booking = response.data.booking;

            setBookingDetails(booking);
            
            // Use existing QR code or generate new one
            if (booking.qrCode) {
              setQrCode(booking.qrCode);
            } else {
              const qrData = `TICKET:${booking.bookingId}|VENUE:${booking.ticketTypeId?.name}|AMOUNT:${booking.totalAmount}|DATE:${booking.visitDate}`;
              setQrCode(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`);
            }
          } else {
            console.log('API returned success=false');
          }
        } catch (apiError) {
          console.error('API Error:', apiError.response?.data || apiError.message);
          
          // Don't use fallback - redirect to check actual booking status
          if (bookingId) {
            console.log('API failed, redirecting to check booking status');
            window.location.href = `/payment-success?bookingId=${bookingId}&amount=${amount}&retry=1`;
          }
        }
      } else {
        console.log('No booking ID found in URL');
      }
    } catch (error) {
      console.error('General error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
        {/* Success Animation */}
        <div className="mb-6">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <span className="text-3xl text-white">✅</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-gray-600">🎉 Congratulations! Your booking has been confirmed. You will receive your e-ticket shortly.</p>
        </div>



        {/* QR Code Ticket */}
        {!loading && bookingDetails && (
          <div id="e-ticket" className={`p-6 rounded-3xl mb-6 border-2 border-dashed ${
            bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket' 
              ? 'bg-gradient-to-br from-yellow-100 via-yellow-50 to-amber-100 border-yellow-400 shadow-xl' 
              : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
          }`}>
            <div className="text-center mb-4">
              {bookingDetails.ticketTypeId?.siteId?.logo && (
                <img 
                  src={`${API_BASE_URL}${bookingDetails.ticketTypeId.siteId.logo}`} 
                  alt="Site Logo" 
                  className="w-16 h-16 mx-auto mb-2 object-contain rounded"
                />
              )}
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                {bookingDetails.ticketTypeId?.siteId?.name}
              </h2>
              <p className="text-sm text-gray-600">
                {bookingDetails.ticketTypeId?.siteId?.headerText || `Welcome to ${bookingDetails.ticketTypeId?.siteId?.name}`}
              </p>
            </div>
            <h3 className={`font-bold mb-4 text-center ${
              bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket'
                ? 'text-yellow-800 text-lg'
                : bookingDetails.ticketTypeId?.category === 'pass_ticket'
                ? 'text-green-800 text-lg'
                : 'text-gray-900'
            }`}>
              {bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket' 
                ? '🏆 Your Gold Pass Ticket' 
                : bookingDetails.ticketTypeId?.category === 'pass_ticket'
                ? '🆔 Your Identity Pass Card'
                : '🎫 Your E-Ticket'
              }
            </h3>
            
            {/* Identity Card - ATM Card Size */}
            {bookingDetails.ticketTypeId?.category === 'pass_ticket' ? (
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #22c55e',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.1)',
                width: '320px',
                height: '200px',
                fontSize: '10px'
              }}>
                {/* Line 1: Site Name and Address */}
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#166534', textAlign: 'center', marginBottom: '2px' }}>
                  {bookingDetails.ticketTypeId?.siteId?.name}
                  {bookingDetails.ticketTypeId?.siteId?.location?.address && (
                    <span style={{ fontSize: '8px', color: '#6b7280', fontWeight: 'normal', display: 'block' }}>
                      {bookingDetails.ticketTypeId?.siteId?.location?.address}
                    </span>
                  )}
                </div>
                
                {/* Line 3: Pass For */}
                <div style={{ fontSize: '9px', fontWeight: '600', color: '#059669', textAlign: 'center', marginBottom: '6px' }}>
                  Pass for: {bookingDetails.ticketTypeId?.name}
                </div>
                
                {/* Line 4: Name */}
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#111827', textAlign: 'center', marginBottom: '8px' }}>
                  {bookingDetails.customerDetails?.name}
                </div>
                
                {/* Line 5: Photo and QR Code */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  {/* Photo */}
                  <div>
                    {bookingDetails.documents?.passportPhoto ? (
                      <img 
                        src={`${API_BASE_URL}/uploads/documents/${bookingDetails.documents.passportPhoto.split('/').pop()}`}
                        alt="Pass Holder" 
                        style={{
                          width: '60px',
                          height: '72px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid white'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '60px',
                        height: '72px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                      }}>
                        👤
                      </div>
                    )}
                  </div>
                  
                  {/* Details */}
                  <div style={{ flex: 1, paddingLeft: '12px', paddingRight: '12px', fontSize: '8px', textAlign: 'left' }}>
                    <div style={{ marginBottom: '2px' }}>
                      <div style={{ color: '#6b7280' }}>Father: {bookingDetails.additionalInfo?.field_0 || 'N/A'}</div>
                    </div>
                    <div style={{ marginBottom: '2px' }}>
                      <div style={{ color: '#6b7280' }}>Mother: {bookingDetails.additionalInfo?.field_1 || 'N/A'}</div>
                    </div>
                    <div style={{ marginBottom: '2px' }}>
                      <div style={{ color: '#6b7280' }}>Emergency: {bookingDetails.additionalInfo?.field_2 || 'N/A'}</div>
                    </div>
                  </div>
                  
                  {/* QR Code */}
                  <div>
                    {qrCode ? (
                      <img 
                        src={qrCode} 
                        alt="QR Code" 
                        style={{ width: '60px', height: '60px', borderRadius: '2px' }}
                      />
                    ) : (
                      <div style={{ width: '60px', height: '60px', backgroundColor: '#f3f4f6', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                        🔲
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Line 8: Issue Date */}
                <div style={{ fontSize: '7px', color: '#9ca3af', textAlign: 'center', paddingTop: '4px', borderTop: '1px solid #e5e7eb' }}>
                  Issue: {new Date(bookingDetails.visitDate).toLocaleDateString()} | ID: {bookingDetails.passId || bookingDetails.bookingId}
                </div>
              </div>
            ) : (
              /* Regular QR Code */
              <div className="text-center mb-4">
                {qrCode ? (
                  <img 
                    src={qrCode} 
                    alt="Ticket QR Code" 
                    className="mx-auto w-32 h-32 border-4 border-white rounded-2xl shadow-lg"
                  />
                ) : (
                  <div className="w-32 h-32 mx-auto bg-gray-200 rounded-2xl flex items-center justify-center">
                    <span className="text-4xl">🎫</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Ticket Details */}
            <div className={`p-4 rounded-2xl text-left space-y-2 text-sm ${
              bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket'
                ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300'
                : 'bg-white'
            }`}>
              <div className="flex justify-between">
                <span className="text-gray-600">Ticket ID:</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{bookingDetails.bookingId}</span>
              </div>
              <div className={`p-2 rounded border-l-4 text-xs mb-2 ${
                bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket'
                  ? 'bg-amber-100 border-amber-500 text-amber-800'
                  : bookingDetails.ticketTypeId?.category === 'pass_ticket'
                  ? 'bg-green-100 border-green-500 text-green-800'
                  : 'bg-yellow-50 border-yellow-400'
              }`}>
                {bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket' ? (
                  <span>🏆 <strong>GOLD PASS - Premium Photo Shoot Access</strong> • Show this QR code at entrance</span>
                ) : bookingDetails.ticketTypeId?.category === 'pass_ticket' ? (
                  <span>🆔 <strong>Identity Pass Card - Valid for {bookingDetails.ticketTypeId?.passTicketSettings?.validityDays || 30} days</strong> • Present this card at entrance</span>
                ) : (
                  <span>⚠️ <strong>Valid for single entry only</strong> • Show this QR code at entrance</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Entry Type:</span>
                <span className={`font-semibold ${
                  bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket'
                    ? 'text-yellow-700 bg-yellow-200 px-2 py-1 rounded text-xs'
                    : bookingDetails.ticketTypeId?.category === 'pass_ticket'
                    ? 'text-green-700 bg-green-200 px-2 py-1 rounded text-xs'
                    : ''
                }`}>
                  {bookingDetails.ticketTypeId?.category === 'photo_shoot_ticket' 
                    ? '🏆 GOLD PASS - PHOTO SHOOT'
                    : bookingDetails.ticketTypeId?.category === 'pass_ticket'
                    ? '🎫 PASS TICKET'
                    : bookingDetails.ticketTypeId?.category?.replace('_', ' ').toUpperCase() || 'ENTRY TICKET'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Customer:</span>
                <span className="font-semibold">{bookingDetails.customerDetails?.name || 'Guest'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Quantity:</span>
                <span className="font-semibold">
                  {bookingDetails.adults && bookingDetails.children ? 
                    `Adult ${bookingDetails.adults}, Child ${bookingDetails.children} (Total ${bookingDetails.quantity})` :
                    bookingDetails.quantity || 1
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-bold text-green-600">₹{bookingDetails.totalAmount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Visit Date:</span>
                <span className="font-semibold">{new Date(bookingDetails.visitDate).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Entry Time:</span>
                <span className="font-semibold text-red-600">9:00 AM - 6:00 PM</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Status:</span>
                <span className="font-bold text-green-600">✅ Confirmed</span>
              </div>
            </div>
            
            {/* Category-Specific Details */}
            <CategoryTicketDisplay 
              booking={bookingDetails} 
              ticket={bookingDetails.ticketTypeId || {}} 
            />
            
            <div className="mt-2 pt-2 border-t text-xs text-gray-500">
              {bookingDetails.ticketTypeId?.siteId?.address && (
                <div className="mb-2">{bookingDetails.ticketTypeId.siteId.address}</div>
              )}
              {bookingDetails.ticketTypeId?.siteId?.contactNumber && (
                <div className="mb-2">📞 {bookingDetails.ticketTypeId.siteId.contactNumber}</div>
              )}
              {bookingDetails.ticketTypeId?.siteId?.website && (
                <div className="mb-2">🌐 {bookingDetails.ticketTypeId.siteId.website}</div>
              )}
              🎟️ Keep this ticket safe • Non-transferable • No refunds
              {bookingDetails.ticketTypeId?.siteId?.footerText && (
                <div className="mt-2 italic">{bookingDetails.ticketTypeId.siteId.footerText}</div>
              )}
            </div>
            
            {/* Powered by Branding */}
            <div className="mt-3 pt-2 border-t border-gray-200 flex items-center justify-center gap-2 text-xs text-gray-400">
              <span>Powered by</span>
              <img src="/Logo1.png" alt="Tiket Jini" className="w-4 h-4 rounded" />
              <span className="font-semibold text-blue-600">Tiket Jini</span>
            </div>
            
            {/* Download Buttons */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={generatePDF}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-xl font-semibold text-sm"
              >
                📄 Download PDF
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 rounded-xl font-semibold text-sm"
              >
                🖨️ Print
              </button>
            </div>
          </div>
        )}
        
        {loading && (
          <div className="bg-gray-50 p-6 rounded-2xl mb-6 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-gray-600">Loading your ticket...</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-2xl font-semibold hover:shadow-lg transition-all"
          >
            📋 View Booking Details
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white py-3 rounded-2xl font-semibold hover:shadow-lg transition-all"
          >
            🎢 Book More Experiences
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 text-xs text-gray-500">
          Thank you for choosing our service! 🙏
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;